import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import BrandMark from "../components/BrandMark";
import { api, API } from "../lib/api";
import { Printer, ArrowLeft, Lock, Download } from "lucide-react";

const CATEGORY_LABELS = {
  macaron: "Macarons", gelato: "Gelato", packaging: "Packaging", supplies: "Supplies",
};

export default function DailyReportDetail() {
  const { id } = useParams();
  const [rep, setRep] = useState(null);

  useEffect(() => { api.get(`/commissary/daily-reports/${id}`).then((r) => setRep(r.data)); }, [id]);

  if (!rep) return <Layout><div className="text-muted-foreground">Loading…</div></Layout>;

  const byCategory = rep.inventory.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="mb-6 flex items-center justify-between no-print">
        <Link to="/activity?tab=daily-reports" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Report history
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              const token = localStorage.getItem("mbp_token");
              const res = await fetch(`${API}/commissary/daily-reports/${id}/csv`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${rep.report_no}_${rep.date}.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
            }}
            data-testid="export-csv-btn"
            className="h-11 px-4 rounded-xl border border-border bg-card font-medium text-sm inline-flex items-center gap-2 hover:bg-secondary"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button
            onClick={() => window.print()}
            data-testid="print-report-btn"
            className="h-11 px-5 rounded-xl bg-foreground text-background font-medium text-sm inline-flex items-center gap-2 hover:bg-foreground/90"
          >
            <Printer className="h-4 w-4" /> Print / PDF
          </button>
        </div>
      </div>

      <article className="luxury-card max-w-4xl mx-auto p-10 sm:p-14 print-page" data-testid="daily-report-page">
        <header className="flex items-start justify-between mb-12">
          <div>
            <BrandMark size="sm" />
            <div className="text-xs text-muted-foreground mt-3">Daily Inventory Report</div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Report</div>
            <div className="font-display text-2xl font-medium tracking-tight">{rep.report_no}</div>
            <div className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1 justify-end">
              <Lock className="h-3 w-3" /> Locked
            </div>
          </div>
        </header>

        <section className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-10 pb-8 border-b border-border">
          <Field label="Location" value={rep.location_name} />
          <Field label="Date" value={rep.date} />
          <Field label="Submitted By" value={rep.submitted_by_name} />
          <Field label="Submitted At" value={new Date(rep.submitted_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} />
        </section>

        {/* Totals */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          {Object.entries(rep.totals).map(([cat, total]) => (
            <div key={cat} className="border border-border rounded-xl p-4">
              <div className="label-eyebrow">{CATEGORY_LABELS[cat] || cat}</div>
              <div className="font-display text-3xl font-light mt-1">{total}</div>
            </div>
          ))}
        </section>

        {/* Inventory by category */}
        {Object.entries(byCategory).map(([cat, items]) => (
          <section key={cat} className="mb-8">
            <h3 className="font-display text-lg font-medium mb-3">{CATEGORY_LABELS[cat] || cat}</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 label-eyebrow font-medium">Item</th>
                  <th className="text-right py-2 label-eyebrow font-medium">On Hand</th>
                  <th className="text-right py-2 label-eyebrow font-medium">Min</th>
                  <th className="text-right py-2 label-eyebrow font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const low = it.current_stock <= it.low_stock_threshold;
                  return (
                    <tr key={it.item_id} className="border-b border-border/60">
                      <td className="py-2.5">
                        <div className="flex items-center gap-2">
                          {it.color && <span className="h-2.5 w-2.5 rounded-full ring-1 ring-border" style={{ background: it.color }} />}
                          {it.name}
                        </div>
                      </td>
                      <td className="py-2.5 text-right font-medium">{it.current_stock} {it.unit}</td>
                      <td className="py-2.5 text-right text-muted-foreground">{it.low_stock_threshold}</td>
                      <td className="py-2.5 text-right">
                        {low ? (
                          <span className="text-[10px] uppercase tracking-wider text-destructive">Low</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">OK</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        ))}

        {/* Adjustments */}
        <section className="mb-4">
          <h3 className="font-display text-lg font-medium mb-3">
            Adjustments Today <span className="text-muted-foreground text-sm">({rep.adjustment_count})</span>
          </h3>
          {rep.adjustments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No adjustments recorded.</p>
          ) : (
            <ul className="divide-y divide-border">
              {rep.adjustments.map((a) => (
                <li key={a.id} className="py-2 text-sm flex items-center justify-between">
                  <div>
                    <span className="font-medium">{a.item_name}</span>
                    <span className="text-muted-foreground"> · {a.user_name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`font-medium ${a.change > 0 ? "text-[#2E7D32]" : "text-destructive"}`}>
                      {a.change > 0 ? "+" : ""}{a.change}
                    </span>
                    <span className="text-muted-foreground text-xs w-16 text-right">
                      {new Date(a.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <footer className="mt-14 pt-6 border-t border-border text-xs text-muted-foreground text-center">
          Locked historical record · Macaron by Patisse · Houston, Texas
        </footer>
      </article>
    </Layout>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="font-medium text-sm">{value}</div>
    </div>
  );
}
